import { Component, OnInit, OnDestroy, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { PaginatorModule } from 'primeng/paginator';
import { MenuModule } from 'primeng/menu';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { MessageService, ConfirmationService } from 'primeng/api';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { Router } from '@angular/router';
import { SystemLogService } from '../../core/services/system-log.service';
import { AuthService } from '../../core/services/auth';
import { environment } from '../../../environments/environment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

export interface SystemLog {
    _id: string;
    userId?: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        refNo: string;
    };
    subscriptionId?: {
        _id: string;
        companyName: string;
    };
    action: string;
    type: string;
    status: string;
    ipAddress: string;
    description: string;
    timestamp: Date;
    userAgent?: string;
    method?: string;
    statusCode?: number;
    requestBody?: any;
    responseBody?: any;
    errorMessage?: string;
    duration?: number;
}

export interface PagedLogsRequest {
    pageIndex: number;
    pageSize: number;
    sortField: string;
    sortOrder: 1 | -1;
    filters: {
        subscriptionId?: string;
        userId?: string;
        action?: string;
        type?: string;
        status?: string;
        ipAddress?: string;
        startDate?: Date;
        endDate?: Date;
        searchText?: string;
    };
    selectedSubscriberId?: string;
}

export interface PagedLogsResponse {
    message: string;
    data: SystemLog[];
    metadata: Array<{
        total: number;
        totalPages: number;
        currentPage: number;
        pageSize: number;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
    }>;
}

@Component({
    selector: 'app-system-logs',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        SelectModule,
        SelectButtonModule,
        PaginatorModule,
        MenuModule,
        ConfirmDialogModule,
        ToastModule,
        CardModule,
        BadgeModule,
        AvatarModule,
        TooltipModule,
        DrawerModule,
        TagModule,
        IconFieldModule,
        InputIcon
    ],
    providers: [MessageService, ConfirmationService],
    templateUrl: './system-logs.html',
    styleUrl: './system-logs.scss'
})
export class SystemLogs implements OnInit, OnDestroy, AfterViewInit {
    logs: SystemLog[] = [];
    totalLogs = 0;
    loading = false;
    currentPage = 1;
    pageSize = 10;
    pageSizes = [10, 20, 50, 100];
    
    // Math property for template
    Math = Math;
    
    // Filters
    searchText = '';
    selectedType: any = null;
    selectedStatus: any = null;
    selectedAction: any = null;
    startDate: Date | null = null;
    endDate: Date | null = null;
    
    // Sorting
    sortField = 'timestamp';
    sortOrder: 1 | -1 = -1; // -1 for descending (newest first), 1 for ascending (oldest first)
    
    // Export settings
    exportRecordCount = 500;
    exportFormat = 'pdf';
    exportFormatOptions = [
        { label: 'PDF', value: 'pdf' },
        { label: 'Excel', value: 'excel' },
        { label: 'CSV', value: 'csv' }
    ];

    // Filter options
    typeOptions = [
        { label: 'All Types', value: null },
        { label: 'Add', value: 'add' },
        { label: 'Update', value: 'update' },
        { label: 'Delete', value: 'delete' },
        { label: 'Login', value: 'login' },
        { label: 'Logout', value: 'logout' },
        { label: 'Refresh', value: 'refresh' },
        { label: 'Reset Password', value: 'reset_password' },
        { label: 'Verify OTP', value: 'verify_otp' },
        { label: 'Send OTP', value: 'send_otp' },
        { label: 'Create', value: 'create' },
        { label: 'View', value: 'view' },
        { label: 'Search', value: 'search' },
        { label: 'Export', value: 'export' },
        { label: 'Import', value: 'import' },
        { label: 'Approve', value: 'approve' },
        { label: 'Deny', value: 'deny' },
        { label: 'Activate', value: 'activate' },
        { label: 'Deactivate', value: 'deactivate' },
        { label: 'Lock', value: 'lock' },
        { label: 'Unlock', value: 'unlock' },
        { label: 'Archive', value: 'archive' },
        { label: 'Restore', value: 'restore' },
        { label: 'Read', value: 'read' }
    ];
    
    statusOptions = [
        { label: 'All Statuses', value: null },
        { label: 'Success', value: 'Success' },
        { label: 'Fail', value: 'Fail' }
    ];
    
    actionOptions = [
        { label: 'All Actions', value: null },
        { label: 'Authentication', value: '/login' },
        { label: 'User Management', value: '/me' },
        { label: 'Customer Management', value: '/customers' },
        { label: 'Product Management', value: '/products' },
        { label: 'Role Management', value: '/roles' },
        { label: 'Permission Management', value: '/permissions' },
        { label: 'Territory Management', value: '/territories' },
        { label: 'Subscriber Management', value: '/subscribers' },
        { label: 'Interaction Management', value: '/interactions' },
        { label: 'Reservation Management', value: '/reservations' },
        { label: 'Contract Management', value: '/contracts' },
        { label: 'Dashboard', value: '/dashboard' },
        { label: 'Customer Lookup', value: '/customer-lookup' },
        { label: 'System Logs', value: '/system-logs' },
        { label: 'FAQ Management', value: '/faqs' },
        { label: 'Online Handbook', value: '/online-handbooks' },
        { label: 'Video Guides', value: '/video-guides' },
        { label: 'Message Templates', value: '/message-templates' },
        { label: 'Payment/Billing', value: '/payment' },
        { label: 'Outlook Calendar', value: '/outlook-calendar' },
        { label: 'Google Calendar', value: '/google-calendar' },
        { label: 'Static Reports', value: '/static-report' },
        { label: 'Simple Reports', value: '/simple-report' },
        { label: 'Custom Reports', value: '/custom-report' },
        { label: 'Calendar', value: '/calendar' },
        { label: 'Files', value: '/files' },
        { label: 'Notifications', value: '/notifications' }
    ];

    constructor(
        private systemLogService: SystemLogService,
        private authService: AuthService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private router: Router,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
        setTimeout(() => {
            this.loadLogs();
        }, 0);
    }

    ngAfterViewInit() {
        // Load logs after view is initialized to avoid change detection errors

    }

    ngOnDestroy() {
        // Cleanup if needed
    }

    loadLogs() {
        if (this.loading) return; // Prevent multiple simultaneous calls
        
        // Defer loading state change to next tick
        setTimeout(() => {
            this.loading = true;
            // this.cdr.detectChanges();
        }, 0);
        
        // Process date range - ensure full day coverage when dates are the same
        let processedStartDate = this.startDate;
        let processedEndDate = this.endDate;
        
        if (this.startDate) {
            processedStartDate = new Date(this.startDate);
            processedStartDate.setHours(0, 0, 0, 0); // Set to beginning of day
        }
        
        if (this.endDate) {
            processedEndDate = new Date(this.endDate);
            processedEndDate.setHours(23, 59, 59, 999); // Set to end of day
        }
        
        // Ensure action filter is properly extracted (handle case where it might be an object)
        let actionFilter: string | undefined = undefined;
        if (this.selectedAction !== null && this.selectedAction !== undefined) {
            actionFilter = typeof this.selectedAction === 'string' 
                ? this.selectedAction 
                : (this.selectedAction as any)?.value || String(this.selectedAction);
        }
        
        const request: PagedLogsRequest = {
            pageIndex: this.currentPage,
            pageSize: this.pageSize,
            sortField: this.sortField,
            sortOrder: this.sortOrder,
            filters: {
                searchText: this.searchText || undefined,
                type: this.selectedType || undefined,
                status: this.selectedStatus || undefined,
                action: actionFilter,
                startDate: processedStartDate || undefined,
                endDate: processedEndDate || undefined
            }
        };

        this.systemLogService.getPagedLogs(request).subscribe({
            next: (response: PagedLogsResponse) => {
                this.logs = response.data;
                if (response.metadata && response.metadata.length > 0) {
                    this.totalLogs = response.metadata[0].total;
                }
                // Defer loading state change to next tick
                setTimeout(() => {
                    this.loading = false;
                    // this.cdr.detectChanges();
                }, 0);
            },
            error: (error) => {
                console.error('Error loading logs:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load system logs'
                });
                // Defer loading state change to next tick
                setTimeout(() => {
                    this.loading = false;
                    // this.cdr.detectChanges();
                }, 0);
            }
        });
    }

    onPageChange(event: any) {
        this.currentPage = event.page + 1;
        this.pageSize = event.rows;
        this.loadLogs();
    }

    onSort(event: any) {
        this.sortField = event.field;
        this.sortOrder = event.order === 1 ? 1 : -1;
        // this.loadLogs();
    }

    applyFilters() {
        this.currentPage = 1;
        this.loadLogs();
    }

    clearFilters() {
        this.searchText = '';
        this.selectedType = null;
        this.selectedStatus = null;
        this.selectedAction = null;
        this.startDate = null;
        this.endDate = null;
        this.currentPage = 1;
        this.exportRecordCount = 500; // Reset to default
        this.exportFormat = 'pdf'; // Reset to default
        this.loadLogs();
    }

    getStatusSeverity(status: string): string {
        return status === 'Success' ? 'success' : 'danger';
    }

    getTypeSeverity(type: string): string {
        switch (type) {
            case 'delete':
            case 'deny':
            case 'deactivate':
            case 'lock':
                return 'danger';
            case 'create':
            case 'approve':
            case 'activate':
            case 'unlock':
                return 'success';
            case 'update':
            case 'modify':
                return 'warning';
            default:
                return 'info';
        }
    }

    formatDuration(duration?: number): string {
        if (!duration) return '-';
        if (duration < 1000) return `${duration}ms`;
        return `${(duration / 1000).toFixed(2)}s`;
    }

    getUserDisplayName(log: SystemLog): string {
        if (log.userId) {
            return `${log.userId.firstName} ${log.userId.lastName}`;
        }
        return 'System';
    }

    getUserInitials(log: SystemLog): string {
        if (log.userId) {
            const firstName = log.userId.firstName?.charAt(0)?.toUpperCase() || '';
            const lastName = log.userId.lastName?.charAt(0)?.toUpperCase() || '';
            return `${firstName}${lastName}`;
        }
        return 'SY'; // System
    }

    getCompanyName(log: SystemLog): string {
        return log.subscriptionId?.companyName || 'N/A';
    }

    exportLogs() {
        try {
            // Validate export record count
            if (!this.exportRecordCount || this.exportRecordCount < 1 || this.exportRecordCount > 10000) {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Please enter a valid number of records (1-10000)'
                });
                return;
            }
            
            this.loading = true;
            
            // Process date range for export - ensure full day coverage when dates are the same
            let exportStartDate = this.startDate;
            let exportEndDate = this.endDate;
            
            if (this.startDate) {
                exportStartDate = new Date(this.startDate);
                exportStartDate.setHours(0, 0, 0, 0); // Set to beginning of day
            }
            
            if (this.endDate) {
                exportEndDate = new Date(this.endDate);
                exportEndDate.setHours(23, 59, 59, 999); // Set to end of day
            }
            
            // Ensure action filter is properly extracted for export
            let exportActionFilter: string | undefined = undefined;
            if (this.selectedAction !== null && this.selectedAction !== undefined) {
                exportActionFilter = typeof this.selectedAction === 'string' 
                    ? this.selectedAction 
                    : (this.selectedAction as any)?.value || String(this.selectedAction);
            }
            
            // Create request with current filters and get all data (no pagination)
            const exportRequest: PagedLogsRequest = {
                pageIndex: 1,
                pageSize: this.exportRecordCount, // Use user-specified record count
                sortField: this.sortField,
                sortOrder: this.sortOrder,
                filters: {
                    subscriptionId: this.authService.getLoggedInUser()?.subscriberId,
                    action: exportActionFilter,
                    type: this.selectedType || undefined,
                    status: this.selectedStatus || undefined,
                    ipAddress: this.searchText || undefined,
                    startDate: exportStartDate || undefined,
                    endDate: exportEndDate || undefined,
                    searchText: this.searchText || undefined
                }
            };

            this.systemLogService.getPagedLogs(exportRequest).subscribe({
                next: (response) => {
                    if (response.data && response.data.length > 0) {
                        switch (this.exportFormat) {
                            case 'pdf':
                                this.generatePDF(response.data);
                                break;
                            case 'excel':
                                this.generateExcel(response.data);
                                break;
                            case 'csv':
                                this.generateCSV(response.data);
                                break;
                            default:
                                this.generatePDF(response.data);
                        }
                    } else {
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Warning',
                            detail: 'No data available for export'
                        });
                    }
                    this.loading = false;
                },
                error: (error) => {
                    console.error('Error exporting logs:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to export system logs'
                    });
                    this.loading = false;
                }
            });
        } catch (error) {
            console.error('Error in exportLogs:', error);
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to export system logs'
            });
            this.loading = false;
        }
    }

    private generatePDF(logs: SystemLog[]) {
        const doc = new jsPDF('landscape', 'mm', 'a4');
        
        // Add title
        doc.setFontSize(18);
        doc.text('System Logs Report', 14, 20);
        
        // Add export info
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 30);
        doc.text(`Total Records: ${logs.length} (Max: ${this.exportRecordCount})`, 14, 35);
        
        // Add filter info if any filters are applied
        let filterInfo = 'Filters: ';
        const activeFilters = [];
        if (this.selectedType) activeFilters.push(`Type: ${this.selectedType}`);
        if (this.selectedStatus) activeFilters.push(`Status: ${this.selectedStatus}`);
        if (this.selectedAction) activeFilters.push(`Action: ${this.selectedAction}`);
        if (this.startDate) activeFilters.push(`Start Date: ${this.startDate.toLocaleDateString()}`);
        if (this.endDate) activeFilters.push(`End Date: ${this.endDate.toLocaleDateString()}`);
        if (this.searchText) activeFilters.push(`Search: ${this.searchText}`);
        
        if (activeFilters.length > 0) {
            filterInfo += activeFilters.join(', ');
            doc.text(filterInfo, 14, 40);
        }

        // Prepare table data
        const tableData = logs.map(log => [
            this.formatTimestamp(log.timestamp),
            this.getUserDisplayName(log),
            log.userId?.email || 'N/A',
            log.action,
            log.type,
            log.status,
            log.description,
            log.errorMessage ? this.truncateText(log.errorMessage, 50) : 'N/A'
        ]);

        // Define table headers
        const headers = [
            'Timestamp',
            'User Name',
            'Email',
            'Action',
            'Type',
            'Status',
            'Description',
            'Error Message'
        ];

        // Generate table
        autoTable(doc, {
            head: [headers],
            body: tableData,
            startY: 50,
            styles: {
                fontSize: 8,
                cellPadding: 2
            },
            headStyles: {
                fillColor: [63, 81, 181],
                textColor: 255,
                fontStyle: 'bold'
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245]
            },
            columnStyles: {
                0: { cellWidth: 35 }, // Timestamp
                1: { cellWidth: 30 }, // User Name
                2: { cellWidth: 40 }, // Email
                3: { cellWidth: 30 }, // Action
                4: { cellWidth: 20 }, // Type
                5: { cellWidth: 20 }, // Status
                6: { cellWidth: 70 }, // Description
                7: { cellWidth: 50 }  // Error Message
            },
            margin: { top: 0, right: 0, bottom: 14, left: 0 }
        });

        // Add footer
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.text(`Page ${i} of ${pageCount}`, 14, doc.internal.pageSize.height - 10);
        }

        // Save the PDF
        const fileName = `system-logs-${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Exported ${logs.length} system logs to PDF`
        });
    }

    private generateExcel(logs: SystemLog[]) {
        // Prepare data for Excel
        const excelData = logs.map(log => ({
            'Timestamp': this.formatTimestamp(log.timestamp),
            'User Name': this.getUserDisplayName(log),
            'Email': log.userId?.email || 'N/A',
            'Action': log.action,
            'Type': log.type,
            'Status': log.status,
            'Description': log.description,
            'Error Message': log.errorMessage || 'N/A'
        }));

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);

        // Set column widths
        const colWidths = [
            { wch: 20 }, // Timestamp
            { wch: 25 }, // User Name
            { wch: 30 }, // Email
            { wch: 25 }, // Action
            { wch: 15 }, // Type
            { wch: 15 }, // Status
            { wch: 50 }, // Description
            { wch: 40 }  // Error Message
        ];
        ws['!cols'] = colWidths;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'System Logs');

        // Generate filename
        const fileName = `system-logs-${new Date().toISOString().split('T')[0]}.xlsx`;
        
        // Save file
        XLSX.writeFile(wb, fileName);

        this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Exported ${logs.length} system logs to Excel`
        });
    }

    private generateCSV(logs: SystemLog[]) {
        // Prepare data for CSV
        const csvData = logs.map(log => ({
            'Timestamp': this.formatTimestamp(log.timestamp),
            'User Name': this.getUserDisplayName(log),
            'Email': log.userId?.email || 'N/A',
            'Action': log.action,
            'Type': log.type,
            'Status': log.status,
            'Description': log.description,
            'Error Message': log.errorMessage || 'N/A'
        }));

        // Create workbook and worksheet for CSV export
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(csvData);
        XLSX.utils.book_append_sheet(wb, ws, 'System Logs');

        // Generate filename
        const fileName = `system-logs-${new Date().toISOString().split('T')[0]}.csv`;
        
        // Save as CSV
        XLSX.writeFile(wb, fileName, { bookType: 'csv' });

        this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Exported ${logs.length} system logs to CSV`
        });
    }

    private formatTimestamp(timestamp: Date | string): string {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleString();
    }

    private truncateText(text: string, maxLength: number): string {
        if (!text) return 'N/A';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    }
}
